import { motion } from 'framer-motion';
import { Wine, Search, Crown, Music, MapPin, Users } from 'lucide-react';

const features = [
  {
    icon: Wine,
    title: 'Explore Nightlife Scene',
    description: 'Discover the hottest parties and nightlife venues in any city',
    color: '#ff84e4',
    bgColor: 'rgba(255, 132, 228, 0.1)',
  },
  {
    icon: Search,
    title: 'Find the Perfect Party',
    description: 'Explore wild club nights, laid-back lounges, or themed events',
    color: '#d987ff',
    bgColor: 'rgba(217, 135, 255, 0.1)',
  },
  {
    icon: Crown,
    title: 'Exclusive VIP Access',
    description: 'Unlock exclusive VIP perks, discounts, and offers at select venues',
    color: '#ffe03d',
    bgColor: 'rgba(255, 224, 61, 0.1)',
  },
  {
    icon: Music,
    title: 'Music Lovers Paradise',
    description: 'Find venues that play your favorite genres all night long',
    color: '#00f5d4',
    bgColor: 'rgba(0, 245, 212, 0.1)',
  },
  {
    icon: MapPin,
    title: 'Local Discovery',
    description: 'Explore hidden gems and trending spots near your location',
    color: '#ff965a',
    bgColor: 'rgba(255, 150, 90, 0.1)',
  },
  {
    icon: Users,
    title: 'Connect & Share',
    description: 'Share experiences and connect with fellow party enthusiasts',
    color: '#ef8172',
    bgColor: 'rgba(239, 129, 114, 0.1)',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const FeaturesSection = () => {
  return (
    <section id="features" className="relative py-24 lg:py-32 bg-[#0a0a0b]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#ff84e4]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#d987ff]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <span className="inline-block font-mono text-xs uppercase tracking-widest text-[#ff84e4] mb-4">
            Features
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Everything You Need for an
            <br />
            <span className="gradient-text">Epic Night Out</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            From discovering hidden gems to VIP access, XtraGlass has everything
            you need to make every night unforgettable
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group relative p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 transition-all duration-500"
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              {/* Icon */}
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: feature.bgColor }}
              >
                <feature.icon size={28} style={{ color: feature.color }} />
              </div>

              {/* Content */}
              <h3 className="font-display text-xl font-bold text-white mb-3 group-hover:text-[#ff84e4] transition-colors">
                {feature.title}
              </h3>
              <p className="text-white/50 leading-relaxed">
                {feature.description}
              </p>

              {/* Hover Glow */}
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"
                style={{ backgroundColor: `${feature.color}10` }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;